import React, { useEffect, useMemo, useState } from 'react';
import { getUserWhereaboutsBreakdown, UsersSingleWhereaboutsBreakdown } from "@features/reporting/services/ReportingService";
import { useSelector } from "react-redux";
import { selectCalendarDate } from "@shared/store/dashboard.duck";
import { findOfficeInOffices } from "@shared/utils/OfficeHelper";
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { SingleWhereaboutsUsage } from "@features/reporting/pages/components/SingleWhereaboutsUsage";
import styled from "styled-components";
import { getListOfDaysBetweenInclusive } from "@shared/utils/DateUtils";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { selectAllWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import {
  FixedWhereabouts,
  FreeBusyStatus,
  UNKNOWN_OPTION,
  WhereaboutsOption
} from "@features/whereabouts/services/WhereaboutOptions";
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import Layout from '@shared/ui/organisms/layout/Layout';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { isCompanyAdminUser, isDepartmentAdmin } from "@shared/utils/AccessControl";
import { UserSearchDropDownList } from '@shared/ui/molecules/UserSearchDropDownList';
import { BasicUserInfo, UserInfo } from "@shared/models/user.models";
import { getParameterByName } from "@shared/utils/UrlUtils";
import { HeadlineMedium } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import { UserContentCard } from '@shared/ui/organisms/UserContentCard';
import { getUserInfo } from '@features/settings/services/ManagementService';
import i18next from "i18next";
import { UsersWhereaboutsPieChart } from "./UsersWhereaboutsPieChart";
import { DownloadReportSection } from "./DownloadReportSection";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { selectAllDepartments } from "@features/whereabouts/pages/departments/data/departments.duck";

const COLOUR = [
  '#3541AE',
  '#5653D2',
  '#4471E5',
  '#88A7F5',
  '#947AC9',
  '#7C43A9',
  '#1C2684',
  '#E772A8',
  '#C03072',
  '#A22597',
  '#C062B8',
  '#EE98E6',
  '#A47FF3',
  '#7FCAF3',
  '#77AACC',
  '#7784CC',
  '#77CCC2',
  '#63ABA37',
  '#64AB63',
  '#D69462',
  '#E47B51',
  '#F6879B',
  '#E3578F',
  '#C23194',
]

function whereaboutsKeyToColour(options: WhereaboutsOption[], key: string): string {
  const option = options.find((wo: WhereaboutsOption) => wo.key === key);
  if (option) {

    if (key === UNKNOWN_OPTION.key) {
      return '#1EE1FF';
    } else if (key === FixedWhereabouts.Office) {
      return '#67E280'
    } else if (key === FixedWhereabouts.NotWorking) {
      return '#BCC6C7'
    } else if (key === FixedWhereabouts.HolidayPending || key === FixedWhereabouts.HolidayApproved || key === FixedWhereabouts.Holiday) {
      return '#FFD027'
    } else if (key === FixedWhereabouts.Home) {
      return '#1AA85C'
    } else {
      const idx = options.indexOf(option);
      if (idx < COLOUR.length) {
        return COLOUR[idx];
      }
      console.log('NotFound', key)
    }
  }

  return '#1EE1FF';
}

export function UsersWhereaboutsReport(props: Props) {
  const calendarDate = useSelector(selectCalendarDate);
  const allOffices = useSelector(selectAllOfficeEntities);
  const currentUser = useSelector(selectCurrentUser);
  const customWhereabouts = useSelector(selectAllWhereaboutsOptions);
  const departments = useSelector(selectAllDepartments);
  const {t} = useTranslation();
  const canUserViewDepartmentReport = isCompanyAdminUser(currentUser) || isDepartmentAdmin(currentUser);

  const isAdmin = isCompanyAdminUser(currentUser);
  const isMySummaryPage = window.location.href.includes('/whereabouts/my-summary');

  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [userInfo, setUserInfo] = useState<undefined | UserInfo>();
  const [reportData, setReportData] = useState<UsersSingleWhereaboutsBreakdown[]>([]);
  const [dateScale, setDateScale] = useState(DateScale.Month);
  const [loading, setLoading] = useState(false);

  document.body.style.backgroundColor = '#FAFAFA';

  useEffect(() => {
    setUserId(getParameterByName('userId', window.location.href) || currentUser?.id);
  }, [currentUser?.id]);

  useEffect(() => {
    if (userId) {
      getUserInfo(userId).then(setUserInfo)
    }
  }, [userId]);

  useEffect(() => {
    // /api/report/user
    if (userId && calendarDate && dateScale) {

      setLoading(true);
      getUserWhereaboutsBreakdown(userId, calendarDate, dateScale)
        .then(data => {
          return data.filter(line => line.key !== FixedWhereabouts.Unknown)
            .map((line: UsersSingleWhereaboutsBreakdown) => {
            const retVal = {
              ...line,
              fill: whereaboutsKeyToColour(customWhereabouts, line.key)
            }
            if (retVal.key === 'OFFICE' && retVal.locationId > 0) {
              const office = findOfficeInOffices(allOffices, retVal.locationId);
              if (office) {
                retVal.label = office.label;
              }
            }
            const option = customWhereabouts.find((wo: WhereaboutsOption) => wo.key === line.key);
            retVal.whereaboutsOption = option;
            return retVal;
          })
        })
        .then(setReportData)
        .catch((err) => {
        })
        .finally(() => setLoading(false));
    }
  }, [calendarDate, dateScale, allOffices, userId, customWhereabouts]);

  const officeData = useMemo(() => {
    if (reportData) {
      return reportData.filter(rd => rd.key === FixedWhereabouts.Office);
    }
    return [];
  }, [reportData]);

  const totalOfficeDays = useMemo(() => {
    return officeData.map(od => od.count)
      .reduce((acc, val) => acc + val, 0);
  }, [officeData])

  const wfhData = useMemo(() => {
    if (reportData) {
      return reportData.filter(rd => rd.key === FixedWhereabouts.Home);
    }
    return [];
  }, [reportData]);

  const leaveData = useMemo(() => {
    if (reportData) {
      return reportData.filter(rd => rd.whereaboutsOption?.freeBusyStatus === FreeBusyStatus.OOF);
    }
    return [];
  }, [reportData]);

  const totalDays = useMemo(() => {
    const dateScaleString = dateScale === DateScale.Month ? 'month' : 'year';
    let days = getListOfDaysBetweenInclusive(calendarDate.clone().startOf(dateScaleString), calendarDate.clone().endOf(dateScaleString));
    if (!currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled) {
      days = days.filter(d => d.isoWeekday() < 6);
    }
    return days.length;
  }, [currentUser, calendarDate, dateScale]);

  const totalIncompleteOrUnknownDays = useMemo(() => {
    const dateScaleString = dateScale === DateScale.Month ? 'month' : dateScale === DateScale.Week ? 'week' : 'year';
    let days = getListOfDaysBetweenInclusive(calendarDate.clone().startOf(dateScaleString), calendarDate.clone().endOf(dateScaleString));
    if (!currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled) {
      days = days.filter(d => d.isoWeekday() < 6);
    }
    const totalDaysInRange = days.length;

    const totalWhereaboutsDays = reportData.map(rd => rd.count)
      .reduce((acc, val) => acc + val, 0);

    if (totalWhereaboutsDays < totalDaysInRange) {
      return totalDaysInRange - totalWhereaboutsDays;
    }
    return 0;
  }, [calendarDate, currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled, dateScale, reportData])

  const otherReportData = useMemo(() => {
    return reportData
      .filter(rd => rd.whereaboutsOption?.freeBusyStatus !== FreeBusyStatus.OOF)
      .filter(rd => rd.key !== FixedWhereabouts.Office)
      .filter(rd => rd.key !== FixedWhereabouts.Home)
  }, [reportData]);

  const pieData = useMemo(() => {
    if (reportData) {
      let retVal = [];

      const dateScaleString = dateScale === DateScale.Month ? 'month' : dateScale === DateScale.Week ? 'week' : 'year';
      let days = getListOfDaysBetweenInclusive(calendarDate.clone().startOf(dateScaleString), calendarDate.clone().endOf(dateScaleString));
      if (!currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled) {
        days = days.filter(d => d.isoWeekday() < 6);
      }
      const totalDaysInRange = days.length;

      const numberOfLeaveDays = leaveData.map(obj => obj.count)
        .reduce((acc, val) => acc + val, 0);
      if (numberOfLeaveDays > 0) {
        retVal.push({value: numberOfLeaveDays, name: 'Leave', fill: '#FFD027', totalDays: totalDaysInRange, })
      }

      const numberOfOfficeDays = officeData.map(obj => obj.count)
        .reduce((acc, val) => acc + val, 0);
      if (numberOfOfficeDays > 0) {
        retVal.push({value: numberOfOfficeDays, name: 'In the Office', fill: '#4DCD67', totalDays: totalDaysInRange,})
      }

      reportData.filter(rd => rd.whereaboutsOption?.freeBusyStatus !== FreeBusyStatus.OOF)
        .filter(rd => rd.key !== FixedWhereabouts.Office)
        .forEach(rd => {
          retVal.push({
            value: rd.count,
            totalDays: totalDaysInRange,
            name: rd.label,
            fill: whereaboutsKeyToColour(customWhereabouts, rd.key),
          });
        })


      if (totalIncompleteOrUnknownDays > 0) {
        retVal.push({
          value: totalIncompleteOrUnknownDays,
          name: 'Incomplete or Unknown',
          totalDays: totalDaysInRange,
          fill: '#1EE1FF',
        })
      }

      return retVal;
    }
    return [];
  }, [calendarDate, currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled, customWhereabouts, dateScale, leaveData, officeData, reportData, totalIncompleteOrUnknownDays]);

  const onUserSelected = (user: BasicUserInfo) => {
    setUserId(user.userId);
  }

  return (
    <Layout fullWidth={true} hideRibbon={true}>
      <Container>
        <Header>

          <ContentWrapper style={{alignItems: 'center', position: 'relative', marginBottom: 12, marginTop: 24}}
                           className={'contentWrapper--header contentWrapper--nav'}>
            <GoBackButton url={isMySummaryPage ? '/whereabouts/team' : '/manage/reporting'} style={{margin: 0}}/>
            {!isMySummaryPage && <ControlButtons>
              {departments.length > 0 && canUserViewDepartmentReport && <ToggleableOutlineButton isActive={false}
                                                                                                 link={'/manage/reporting/whereabouts?view=department'}
                                                                                                 text={'button.department-report'}/>}
              <ToggleableOutlineButton isActive={false}
                                       link={'/manage/reporting/whereabouts?view=team'}
                                       text={'button.team-report'}/>
              <ToggleableOutlineButton isActive={false}
                                       link={'/manage/reporting/whereabouts?view=workgroup'}
                                       text={'button.workgroup-report'}/>
              <ToggleableOutlineButton link={"/manage/user/whereabouts-summary"}
                                       isActive={true}
                                       text={'button.user-report'}/>
            </ControlButtons>}
          </ContentWrapper>

          <ContentWrapper style={{alignItems: 'center'}} className={'contentWrapper--header'}>
            <DateFlicker dateScale={dateScale} updateGlobalDate={true} showDateRange={true} />
            <Buttons>
              <ToggleableOutlineButton isActive={dateScale === DateScale.Year} click={() => setDateScale(DateScale.Year)} text={'button.year-view'} />
              <ToggleableOutlineButton isActive={dateScale === DateScale.Month} click={() => setDateScale(DateScale.Month)} text={'button.month-view'} />
              <ToggleableOutlineButton isActive={dateScale === DateScale.Week} click={() => setDateScale(DateScale.Week)} text={'button.week-view'} />
            </Buttons>
          </ContentWrapper>
        </Header>

        {isAdmin && <ContentWrapper className={'contentWrapper--main'}>
          <AdminHeader>
            <UserSearchDropDownList closeOnSelect={true} onSelect={onUserSelected} />
          </AdminHeader>
        </ContentWrapper>}

        <ContentWrapper className={'contentWrapper--main'}>
          <HeadlineMedium style={{marginBottom: 16}}>{t('reports-page.whereabouts-report.title')}</HeadlineMedium>
        </ContentWrapper>

        {loading ? <>
          <Center>
            <LoadingSpinner hideBorder={true} hideText={true} />
          </Center>
        </> : <>
          <ContentWrapper className={'contentWrapper--main'}>

            <Left>
              {userInfo && <UserContentCard userId={userInfo.userId}
                                            role={userInfo.role}
                                            email={userInfo.email}
                                            jobTitle={userInfo.jobTitle}
                                            lastName={userInfo.lastName}
                                            firstName={userInfo.firstName}
                                            hideButton={true}/>}


              {wfhData.map((line, key) => (
                <SingleWhereaboutsUsage key={key}
                                        totalDays={totalDays}
                                        usedDays={line.count}
                                        label={line.label}
                                        icon={line.icon}
                                        fillColour={line.fill}
                                        whereaboutsKey={line.key}
                                        style={{borderBottom: 'none'}} />
              ))}

              {wfhData.length > 0 && <HorizontalSpacer style={{marginBottom: 20}} />}

              {!!totalOfficeDays && <>
                <SingleWhereaboutsUsage totalDays={totalOfficeDays}
                                        usedDays={totalOfficeDays}
                                        label={i18next.t('reports-page.holidays-report.office')}
                                        icon={officeData[0]?.icon}
                                        fillColour={whereaboutsKeyToColour(customWhereabouts, FixedWhereabouts.Office)}
                                        whereaboutsKey={officeData[0]?.key}
                                        style={{borderBottom: 'none'}} />
              </>}
              {officeData.map((line, key) => (
                <SingleWhereaboutsUsage key={key}
                                        totalDays={totalDays}
                                        usedDays={line.count}
                                        label={line.label}
                                        icon={line.icon}
                                        hideIcon={true}
                                        whereaboutsKey={line.key}
                                        fillColour={line.fill}
                                        className={'whereaboutsUsage__office'}
                                        style={{borderBottom: 'none'}} />
              ))}

              {officeData.length > 0 && <HorizontalSpacer style={{marginBottom: 20}} />}

              {leaveData.map((line, key) => (
                <SingleWhereaboutsUsage key={key}
                                        totalDays={totalDays}
                                        usedDays={line.count}
                                        label={line.label}
                                        icon={line.icon}
                                        fillColour={line.fill}
                                        whereaboutsKey={line.key}
                                        style={{borderBottom: 'none'}} />
              ))}

              {leaveData.length > 0 && <HorizontalSpacer style={{marginBottom: 20}} />}

              {otherReportData.map((line, key) => (
                <>
                  <SingleWhereaboutsUsage key={key}
                                          totalDays={totalDays}
                                          usedDays={line.count}
                                          label={line.label}
                                          icon={line.icon}
                                          fillColour={line.fill}
                                          whereaboutsKey={line.key}
                                          style={{borderBottom: 'none'}} />
                  <HorizontalSpacer style={{marginBottom: 20}} />
                </>
              ))}

              {totalIncompleteOrUnknownDays > 0 && <>
                <SingleWhereaboutsUsage key={FixedWhereabouts.Unknown}
                                        totalDays={totalDays}
                                        usedDays={totalIncompleteOrUnknownDays}
                                        label={'Unknown or Incomplete'}
                                        icon={''}
                                        fillColour={whereaboutsKeyToColour(customWhereabouts, FixedWhereabouts.Unknown)}
                                        whereaboutsKey={FixedWhereabouts.Unknown}
                                        style={{borderBottom: 'none'}} />
              </>}

              {isAdmin && <DownloadReportSection selectedUserId={userInfo?.userId} />}
            </Left>
            <Right>
              <UsersWhereaboutsPieChart data={pieData} />
            </Right>
          </ContentWrapper>
        </>}
      </Container>
    </Layout>
  )
}

interface Props {
  dateScale?: DateScale;
}

const Container = styled.div`
  min-height: 100vh;
  margin-bottom: 80px;
  .chart__background {
    filter: drop-shadow(0px 9px 22px 0px rgba(46, 74, 148, 0.08));
  }
  @media (${tablet}) {
    padding: 0;
  }
  .contentWrapper--main {
    padding: 0 16px;
    @media (${tablet}) {
      padding: 0;
    }
  }
`

const Header = styled.div`
  width: 100%;
  background-color: white;
  box-shadow: 0 9px 22px 0 rgba(46, 74, 148, 0.08);
  min-height: 50px;
  padding: 6px 16px;
  margin-bottom: 24px;
  .contentWrapper--header {
    flex-direction: column;
  }
  @media (${tablet}) {
    .contentWrapper--header {
      flex-direction: row;
    }
  }
  
  .contentWrapper--nav {
    .goBackButton {
      margin: 16px 0 16px 0;
      @media (${tablet}) {
        align-self: center;
      }
    }
  }
`

const ContentWrapper = styled.div`
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  flex-direction: row;
`

const Buttons = styled.div`
  display: flex;
  margin-left: auto;
`

const Left = styled.div`
  width: 100%;
  .whereaboutsUsage__office p, .whereaboutsUsage__office h3 {
    font-size: 14px;
  }
  @media (${tablet}) {
    width: 60%;
  }
`

const Right = styled.div`
  height: 50vh;
  display: none;
  @media (${tablet}) {
    display: block;
    width: 40%;
  }
`

const AdminHeader = styled.div`
  margin-bottom: 24px;
  width: 400px;
  max-width: 100%;
`

const ControlButtons = styled.div`
  display: flex;
  align-self: center;
  justify-self: center;
  margin-left: auto;
  margin-right: auto;
  max-width: 100%;
  overflow-x: auto;
  margin-top: 12px;
  margin-bottom: 12px;
  @media (${tablet}) {
    margin-top: 0;
  }
`
